import React, { useEffect } from 'react'
import { Paper, Grid, TextField, Button, Typography, CircularProgress } from '@material-ui/core'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ChipInput from 'material-ui-chip-input'
import EditIcon from '@material-ui/icons/Edit'
import { useStore } from '../../context/Context'
import { updateMovie } from '../../api'
import { useHistory, useParams } from 'react-router-dom'
import Cookie from 'js-cookie'
import useStyles from './styles'

const RenderImage = React.memo(({ src }) => {
  const classes = useStyles()

  return (
    <Grid item>
      <div
        className={classes.image_urlStyle}
        style={{
          backgroundImage: `url(${src})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
    </Grid>
  )
})

const MoviesEdit = () => {
  const classes = useStyles()
  const history = useHistory()
  const [initialImage, setInitialImage] = React.useState('')
  const { id } = useParams()

  const { setIsLoading, isLoading, setListMovies, listMovies, setMessage } = useStore()
  const { handleSubmit, getFieldProps, errors, touched, resetForm, setValues, values } = useFormik({
    initialValues: {
      title: '',
      description: '',
      year: 2021,
      duration: 0,
      genre: [],
      rating: 0,
      review: '',
      image_url: ''
    },
    validationSchema: Yup.object({
      title: Yup.string().required('This field is a must!'),
      description: Yup.string().required('This field is a must!'),
      year: Yup.number().typeError('Must be a number!').required('This field is a must!').min(1900).max(new Date().getFullYear()),
      duration: Yup.number().typeError('Must be a number!').required('This field is a must!'),
      genre: Yup.array().min(1, 'At least one item in this field!').required(),
      rating: Yup.number().typeError('Must be a number!').required('This field is a must!').min(0).max(10),
      review: Yup.string().required('This field is a must!'),
      image_url: Yup.string().url('Must be an image URL!').required('This field is a must!'),
    }),
    onSubmit: async (values, actions) => {
      setIsLoading(true)
      const genre = values.genre.join(', ')
      const newData = { ...values, genre }
      try {
        const token = await Cookie.get('token')
        const { data } = await updateMovie(id, newData, token)
        setListMovies([
          ...listMovies,
          data
        ])
        actions.resetForm()
        setMessage(state => ({
          ...state,
          msg: `${id} has been updated`,
          severty: 'success',
          open: true
        }))
        history.replace('/moviesDb')
        setIsLoading(false)
      } catch (error) {
        console.log(error)
        setMessage(state => ({
          ...state,
          msg: error.message,
          severty: 'error',
          open: true,
          link: '/moviesDb'
        }))
        setIsLoading(false)
      }
    }
  })

  useEffect(() => {
    if (listMovies.length) {
      const editData = listMovies.find(movie => movie.id == id)
      const { title, description, year, duration, rating, review, image_url } = editData
      const genre = editData.genre.split(',').map(gen => gen.trim())
      setInitialImage(image_url)
      setValues({
        title,
        description,
        year,
        duration,
        genre,
        rating,
        review,
        image_url
      })
    }
  }, [id, listMovies, setValues, setInitialImage])

  const handleAddGenreChip = (chip) => setValues({ ...values, genre: [...values.genre, chip] })

  const handleDeleteGenreChip = (chip) => {
    const newData = values.genre.filter(genre => genre !== chip)
    setValues({ ...values, genre: newData })
  }

  return (
    <main className={classes.root}>
      <form onSubmit={handleSubmit} autoComplete="off">
        <Paper elevation={0} className={classes.paper} style={{ minHeight: 600 }}>
          {isLoading ? (
            <Grid container justifyContent="center" alignItems="center" className={classes.loading}>
              <CircularProgress />
            </Grid>
          ) : (
            <Grid container direction="column" spacing={3}>
              <Grid item>
                <TextField variant="outlined" label="Title" fullWidth {...getFieldProps('title')} />
                {touched.title && errors.title ? <Typography variant="caption" color="error">{errors.title}</Typography> : null}
              </Grid>
              <Grid item container direction="column" spacing={2} style={{ marginBottom: '1em' }}>
                <Grid item>
                  <ChipInput
                    value={values.genre}
                    onAdd={(chip) => handleAddGenreChip(chip)}
                    onDelete={(chip) => handleDeleteGenreChip(chip)}
                    label="Genre"
                    helperText="[ Enter ] to add new item"
                    className={classes.chipInput}
                  />
                </Grid>
                <Grid item>
                  {touched.genre && errors.genre ? <Typography variant="caption" color="error">{errors.genre}</Typography> : null}
                </Grid>
              </Grid>
              <Grid item>
                <TextField multiline minRows={5} variant="outlined" label="Description" fullWidth {...getFieldProps('description')} />
                {touched.description && errors.description ? <Typography variant="caption" color="error">{errors.description}</Typography> : null}
              </Grid>
              <Grid item>
                <Grid item container direction="row" spacing={3}>
                  <Grid item container direction="column" style={{ width: 'auto' }}>
                    <TextField variant="outlined" label="Year" {...getFieldProps('year')} />
                    {touched.year && errors.year ? <Typography variant="caption" color="error">{errors.year}</Typography> : null}
                  </Grid>
                  <Grid item container direction="column" style={{ width: 'auto' }}>
                    <TextField variant="outlined" label="Duration" {...getFieldProps('duration')} />
                    {touched.duration && errors.duration ? <Typography variant="caption" color="error">{errors.duration}</Typography> : null}
                  </Grid>
                  <Grid item container direction="column" style={{ width: 'auto' }}>
                    <TextField variant="outlined" label="Rating" {...getFieldProps('rating')} />
                    {touched.rating && errors.rating ? <Typography variant="caption" color="error">{errors.rating}</Typography> : null}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <TextField variant="outlined" label="Review" fullWidth {...getFieldProps('review')} />
                {touched.review && errors.review ? <Typography variant="caption" color="error">{errors.review}</Typography> : null}
              </Grid>
              <Grid item>
                <TextField multiline minRows={2} variant="outlined" label="Image URL" fullWidth {...getFieldProps('image_url')} />
                {touched.image_url && errors.image_url ? <Typography variant="caption" color="error">{errors.image_url}</Typography> : null}
              </Grid>
              {initialImage && (
                <RenderImage src={initialImage} />
              )}
              <Grid item>
                <Grid item container direction="row" spacing={3}>
                  <Grid item>
                    <Button type="submit" variant="contained" className={classes.buttonSubmit}><EditIcon />&nbsp;&nbsp;Update Movie</Button>
                  </Grid>
                  <Grid item>
                    <Button onClick={resetForm} variant="outlined" className={classes.buttonReset}>Reset Form</Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Paper>
      </form>
    </main>
  )
}

export default MoviesEdit
